import { __decorate, __metadata } from "tslib";
import { formatSeconds } from '@1inch-community/core/formatters';
import { lazyAppContextConsumer } from '@1inch-community/core/lazy';
import { dispatchEvent, observe, translate } from '@1inch-community/core/lit-utils';
import { getSymbolFromWrapToken } from '@1inch-community/sdk/chain';
import { SwapContextToken } from '@1inch-community/sdk/swap';
import { buildTokenId, buildTokenIdByToken, isRateEqual, isTokensEqual, } from '@1inch-community/sdk/tokens';
import '@1inch-community/ui-components/button';
import '@1inch-community/ui-components/icon';
import { consume } from '@lit/context';
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { when } from 'lit/directives/when.js';
import { debounceTime, defer, distinctUntilChanged, map, shareReplay, startWith, switchMap, withLatestFrom, } from 'rxjs';
import { fusionSwapInfoMainStyle } from './fusion-swap-info-main.style';
let FusionSwapInfoMainElement = class FusionSwapInfoMainElement extends LitElement {
    constructor() {
        super(...arguments);
        this.isOpen = false;
        this.applicationContext = lazyAppContextConsumer(this);
        this.rate$ = defer(() => this.getContext().rate$);
        this.minReceive$ = defer(() => this.getContext().minReceive$);
        this.chainId$ = defer(() => this.getContext().chainId$);
        this.destinationToken$ = defer(() => this.getContext().getTokenByType('destination'));
        this.slippage$ = defer(() => this.getContext().slippage$).pipe(map((slippage) => {
            return html `
        ${slippage.value === null ? '' : html `<span>${slippage.value}% · </span>`}
        <span>
          ${when(slippage.type === 'auto', () => 'Auto')}
          ${when(slippage.type === 'custom', () => 'Custom')}
          ${when(slippage.type === 'preset', () => 'Manual')}
        </span>
      `;
        }));
        this.auctionTime$ = defer(() => this.getContext().auctionTime$).pipe(map((auctionTime) => {
            return html `
        ${auctionTime.value === null
                ? ''
                : html `<span>${formatSeconds(auctionTime.value)} · </span>`}
        <span>
          ${when(auctionTime.type === 'auto', () => 'Auto')}
          ${when(auctionTime.type === 'custom', () => 'Custom')}
          ${when(auctionTime.type === 'preset', () => 'Manual')}
        </span>
      `;
        }));
        this.rateView$ = this.rate$.pipe(debounceTime(0), distinctUntilChanged(rateViewDistinctUntilChangedHandler), switchMap(async (rateData) => {
            if (rateData === null)
                return this.getLoadRateView();
            const { rate, revertedRate, sourceToken, destinationToken } = rateData;
            const primaryToken = await this.applicationContext.value.tokenStorage.getPriorityToken(sourceToken.chainId, [sourceToken.address, destinationToken.address]);
            const secondaryToken = isTokensEqual(primaryToken, sourceToken)
                ? destinationToken
                : sourceToken;
            const isRevertedRate = isTokensEqual(primaryToken, sourceToken);
            const targetRate = isRevertedRate ? revertedRate : rate;
            const rateFormated = targetRate.toFixedSmart(2);
            const tokenPrice = await this.applicationContext.value.tokenStorage.getTokenFiatPrice({
                tokenRecordId: buildTokenIdByToken(secondaryToken),
            });
            const rateFiatFormated = tokenPrice.toFixedSmart(2);
            return html `
        <span class="rate-view"
          >1 ${getSymbolFromWrapToken(secondaryToken)} = ${rateFormated} ${primaryToken.symbol}
          <span class="dst-token-rate-usd-price">~$${rateFiatFormated}</span></span
        >
      `;
        }), startWith(this.getLoadRateView()), shareReplay({ bufferSize: 1, refCount: true }));
        this.minReceiveView$ = this.minReceive$.pipe(withLatestFrom(this.destinationToken$, this.chainId$), switchMap(async ([minReceive, dstToken, chainId]) => {
            if (!dstToken || !chainId)
                return html ``;
            const tokenPrice = await this.applicationContext.value.tokenStorage.getTokenFiatPrice({
                tokenRecordId: buildTokenId(chainId, dstToken.address),
            });
            const amountFiat = minReceive.mul(tokenPrice);
            const amountFiatFormated = amountFiat.toFixedSmart(2);
            return html `
        <span>~$${amountFiatFormated}</span>
        <span class="min-receive">${minReceive.toFixedSmart(6)} ${dstToken.symbol}</span>
      `;
        }));
    }
    static { this.tagName = 'inch-fusion-swap-info-main'; }
    static { this.styles = fusionSwapInfoMainStyle; }
    connectedCallback() {
        super.connectedCallback();
        dispatchEvent(this, 'changeFusionInfoOpenState', this.isOpen);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        dispatchEvent(this, 'changeFusionInfoOpenState', false);
    }
    render() {
        const classes = {
            container: true,
            open: this.isOpen,
        };
        const iconClasses = {
            icon: true,
            'open-icon': this.isOpen,
        };
        const fusionIconClass = {
            'fusion-icon': true,
            'fusion-icon-open': this.isOpen,
        };
        return html `
      <div
        class="${classMap(classes)}"
        @click="${() => {
            if (this.isOpen)
                return;
            this.isOpen = true;
            dispatchEvent(this, 'changeFusionInfoOpenState', this.isOpen);
        }}"
      >
        <div class="rate-container">${observe(this.rateView$)}</div>
        <div class="fusion-info">
          <div class="${classMap(fusionIconClass)}">
            <inch-icon icon="fusion16"></inch-icon>
            <span>Free</span>
          </div>
          <inch-button
            @click="${(event) => this.onChangeOpen(event)}"
            size="l"
            type="tertiary"
          >
            <inch-icon class="${classMap(iconClasses)}" icon="chevronDown16"></inch-icon>
          </inch-button>
        </div>
        <div class="content-container">
          <div class="content-row">
            <span class="row-title"
              >${translate('widgets.swap-form.fusion-info.slippage-tolerance')}</span
            >
            <div
              @click="${() => dispatchEvent(this, 'openSlippageSettings', null)}"
              class="row-content row-slippage"
            >
              ${observe(this.slippage$)}
            </div>
          </div>
          <div class="content-row">
            <span class="row-title"
              >${translate('widgets.swap-form.fusion-info.auction-time')}</span
            >
            <div
              @click="${() => dispatchEvent(this, 'openAuctionTimeSettings', null)}"
              class="row-content row-slippage"
            >
              ${observe(this.auctionTime$)}
            </div>
          </div>
          <div class="content-row">
            <span class="row-title">${translate('widgets.swap-form.fusion-info.min-receive')}</span>
            <div class="row-content">${observe(this.minReceiveView$)}</div>
          </div>
          <div class="content-row">
            <span class="row-title">${translate('widgets.swap-form.fusion-info.net-fee')}</span>
            <div class="row-content">
              <inch-icon icon="fusion16"></inch-icon>
              <span>Free</span>
            </div>
          </div>
        </div>
      </div>
    `;
    }
    getLoadRateView() {
        return html ` <div class="rate-loader"></div> `;
    }
    onChangeOpen(event) {
        event.preventDefault();
        event.stopPropagation();
        this.isOpen = !this.isOpen;
        dispatchEvent(this, 'changeFusionInfoOpenState', this.isOpen);
    }
    getContext() {
        if (!this.context)
            throw new Error('');
        return this.context;
    }
};
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FusionSwapInfoMainElement.prototype, "isOpen", void 0);
__decorate([
    consume({ context: SwapContextToken }),
    __metadata("design:type", Object)
], FusionSwapInfoMainElement.prototype, "context", void 0);
FusionSwapInfoMainElement = __decorate([
    customElement(FusionSwapInfoMainElement.tagName)
], FusionSwapInfoMainElement);
export { FusionSwapInfoMainElement };
function rateViewDistinctUntilChangedHandler(rate1, rate2) {
    if (rate1 === null || rate2 === null) {
        return false;
    }
    return isRateEqual(rate1, rate2);
}
//# sourceMappingURL=fusion-swap-info-main.element.js.map